import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { BrandLogo } from '../../components/BrandLogo';
import { getSupabaseClient } from '../../lib/supabase';
import { env } from '../../lib/env';
import { getSupabaseRuntimeIdentity } from '../../features/runtime/supabaseAdapter';

function roleHome(roles: Array<{ role: string }>) {
  if (roles.some((item) => item.role === 'student')) return '/student';
  if (roles.some((item) => item.role === 'teacher')) return '/teacher/students';
  if (roles.some((item) => item.role === 'guardian')) return '/parent/progress';
  if (roles.some((item) => item.role === 'branch_admin' || item.role === 'superadmin')) return '/admin';
  return '/';
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const client = useMemo(() => getSupabaseClient(), []);
  const next = new URLSearchParams(location.search).get('next');

  useEffect(() => {
    if (!client) return;
    void client.auth.getSession().then(async ({ data }) => {
      if (!data.session) return;
      try {
        const identity = await getSupabaseRuntimeIdentity();
        navigate(next || roleHome(identity?.roles ?? []), { replace: true });
      } catch {
        // Keep the form visible if identity is not provisioned yet.
      }
    });
  }, [client, navigate, next]);

  async function login() {
    if (!client) {
      setStatus('error');
      setMessage('Supabase belum dikonfigurasi pada build ini.');
      return;
    }
    setStatus('loading');
    setMessage('');
    const result = await client.auth.signInWithPassword({ email: email.trim(), password });
    if (result.error) {
      setStatus('error');
      setMessage(result.error.message);
      return;
    }
    try {
      const identity = await getSupabaseRuntimeIdentity();
      setStatus('idle');
      navigate(next || roleHome(identity?.roles ?? []), { replace: true });
    } catch (error) {
      setStatus('error');
      setMessage(error instanceof Error ? error.message : 'Login berhasil, tetapi Student/Role Record belum tersedia.');
    }
  }

  return (
    <main className="app-main auth-page">
      <section className="card auth-card stack">
        <BrandLogo />
        <div>
          <div className="eyebrow">BelajarKu</div>
          <h1>Masuk</h1>
          <p className="muted">Gunakan akun yang sudah terdaftar. Data belajar dibatasi sesuai role dan hubungan siswa.</p>
        </div>
        {!env.supabaseUrl || !env.supabasePublishableKey ? (
          <div className="feedback feedback--error">Build ini belum mempunyai konfigurasi Supabase.</div>
        ) : null}
        <label>
          <strong>Email</strong>
          <input className="answer-input" type="email" autoComplete="username" value={email} onChange={(event) => setEmail(event.target.value)} />
        </label>
        <label>
          <strong>Password</strong>
          <input className="answer-input" type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') void login(); }} />
        </label>
        {message && <div className="feedback feedback--error">{message}</div>}
        <div className="button-row">
          <button className="btn btn--primary btn--large" type="button" disabled={status === 'loading' || !email.trim() || !password} onClick={() => void login()}>
            {status === 'loading' ? 'Memeriksa…' : 'Masuk'}
          </button>
          <Link className="btn" to="/">Kembali</Link>
        </div>
        <small className="muted">BelajarKu tidak pernah meminta Secret Key, service_role, atau database password pada halaman login.</small>
      </section>
    </main>
  );
}
